import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation  } from 'react-router-dom';
import { openPlans } from '../../libs/platform';
import './style.css';
import HelpWidget from './HelpWidget';
import CelebrationModal from './CelebrationModal';
import { MicrophoneHelper, useMicrophoneHelper } from './MicrophoneHelper';
import Bussola from './bussola/Bussola';
import AnimatedAtaric from '../shared/AnimatedAtaric';
import LoadingPet from '../shared/LoadingPet';

import http from '../../libs/http';
import recognition from '../../libs/recognition';
import recorder from '../../libs/recorder';
import stt from '../../libs/stt';
import voice from '../../libs/voice';
import tts from '../../libs/tts';
import alert from '../../libs/alert';
import analytics from '../../libs/analytics';

// Flag to switch between STT providers: 'native' (Web Speech API) | 'server' (Deepgram Nova-3)
const STT_PROVIDER: 'native' | 'server' = 'server';

// Helper to check if error is timeout/network related
const isTimeoutError = (error: string) => {
  const errorLower = (error || '').toLowerCase();
  return (
    errorLower.includes('timeout') ||
    errorLower.includes('timed out') ||
    errorLower.includes('too large') ||
    errorLower.includes('networkerror') ||
    errorLower.includes('network error') ||
    errorLower.includes('fetch') ||
    errorLower.includes('aborted')
  );
};

let audioCtx: any;
if (typeof window !== 'undefined' && !audioCtx) {
  const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
  if (AudioContextClass) {
    audioCtx = new AudioContextClass();
  } else {
    console.warn('⚠️ Web Audio API is not supported in this browser');
  }
}

let audioUnlocked = false;

function Chat({ user, setUser }: any) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [hasStarted, setHasStarted] = useState(false);
  const [listening, setListening] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [showTranscript, setShowTranscript] = useState(true);
  const [answerToTranscript, setAnswerToTranscript] = useState('');
  const [lastTranscript, setLastTranscript] = useState('');
  const [transcript, setTranscript] = useState('');
  const transcriptRef = useRef('');
  const [messages, setMessages] = useState<any[]>([]);
  const [language, setLanguage] = useState(voice.languages.enUS);
  const [conversationId, setConversationId] = useState<number | null>(null);
  
  // Celebration state
  const [showCelebration, setShowCelebration] = useState(false);
  const [messagesThisSession, setMessagesThisSession] = useState(0);
  const [totalMessages, setTotalMessages] = useState(0);
  
  // Microphone helper state
  const { micError, setError: setMicError, clearError: clearMicError, audioLevel, setAudioLevel } = useMicrophoneHelper();

  // Bússola (language mind-map) state
  const [bussolaOpen, setBussolaOpen] = useState(false);

  // Ao abrir o chat: mostrar só o monstrinho carregando e abrir direto na fala do Ataric (sem mostrar "Iniciar" nem "Clique para Falar" vazio)
  const [initialChatLoading, setInitialChatLoading] = useState(true);

  function changeStateAfterStartRec() {
    setListening(true);
  }

  function changeStateAfterStopRec() {
    setListening(false);
    setTranscript('');
    setAudioLevel(0); // Reset audio level indicator
    // Only clear recognition text if using native provider
    if (STT_PROVIDER === 'native') {
      recognition.clearText();
    }
  }

  function cancelRec() {
    try {
      if (STT_PROVIDER === 'server') {
        recorder.stop().catch(() => {});
      } else {
        recognition.stop();
      }
    } catch (_) {
      // Ignore errors when stopping - recorder/recognition might not be started
    }
    changeStateAfterStopRec();
    setFetching(false);
  }

  async function rec() {
    try {
      console.log('rec() called, listening:', listening, 'fetching:', fetching, 'provider:', STT_PROVIDER);
      
      if (!listening) {
        console.log('Starting recognition...');
        setFetching(false);
        setTranscript('');
        transcriptRef.current = '';
        
        // Interrupt any ongoing assistant audio before recording.
        try { tts.pause(); } catch (_) {}
        try { voice.stop(); } catch (_) {}
        
        if (STT_PROVIDER === 'server') {
          // Use recorder with platform-optimized settings
          try {
            const platform = recorder.getPlatformInfo();
            console.log('Starting recorder on:', platform.description);
            clearMicError(); // Clear any previous errors
            
            await recorder.start({
              onAudioLevel: (level) => {
                setAudioLevel(level);
              }
            });
            
            setTranscript(''); // Clear transcript, we show audio level indicator instead
          } catch (recError: any) {
            console.error('Recorder start failed:', recError);
            
            // Show visual error modal instead of just alert
            const message = recError?.message?.toLowerCase() || '';
            if (message.includes('https') || message.includes('secure')) {
              setMicError('https-required');
            } else if (message.includes('not supported') || message.includes('not available')) {
              setMicError('not-supported');
            } else if (message.includes('permission') || message.includes('denied') || message.includes('not allowed')) {
              setMicError('permission-denied');
            } else if (message.includes('not found') || message.includes('no device')) {
              setMicError('mic-not-found');
            } else if (message.includes('in use') || message.includes('not readable')) {
              setMicError('mic-in-use');
            } else {
              setMicError('generic-error');
            }
            
            setFetching(false);
            return;
          }
        } else {
          // Use Native Web Speech API
          recognition.stop();
          recognition.rec((text: string) => {
            transcriptRef.current = text;
            setTranscript(text);
          });
        }
        
        changeStateAfterStartRec();
        return;
      }
  
      console.log('Stopping recognition...');
      setFetching(true);
      setListening(false);
      
      let toSend = '';
      
      if (STT_PROVIDER === 'server') {
        // Stop recording and get audio blob
        const audioBlob = await recorder.stop();
        const wasAudioDetected = recorder.wasAudioDetected();
        const peakLevel = recorder.getPeakLevel();
        const platform = recorder.getPlatformInfo();
        
        console.log('Audio recorded:', { 
          size: audioBlob.size, 
          type: audioBlob.type,
          wasAudioDetected,
          peakLevel: peakLevel.toFixed(3),
          platform: platform.description
        });
        
        // Enhanced validation for different failure modes
        const minSize = platform.isIOS ? 2000 : 1000; // iOS WAV files are larger
        
        if (audioBlob.size < minSize) {
          // Very small file - likely instant stop or permission issue
          setMicError('no-audio-detected');
          setFetching(false);
          changeStateAfterStopRec();
          return;
        }
        
        // Check if we detected actual audio (not just silence)
        if (!wasAudioDetected && peakLevel < 0.02) {
          // Recording exists but no audio detected - mic capturing silence
          setMicError('silent-recording');
          setFetching(false);
          changeStateAfterStopRec();
          return;
        }
        
        // Helper to split blob in half
        const splitBlob = async (blob: Blob): Promise<[Blob, Blob]> => {
          const arrayBuffer = await blob.arrayBuffer();
          const midpoint = Math.floor(arrayBuffer.byteLength / 2);
          const firstHalf = new Blob([arrayBuffer.slice(0, midpoint)], { type: blob.type });
          const secondHalf = new Blob([arrayBuffer.slice(midpoint)], { type: blob.type });
          return [firstHalf, secondHalf];
        };
        
        // Send to STT via backend
        setTranscript('🔄 Transcrevendo...');
        let result = await stt.transcribe(audioBlob, language, user.token);
        
        // If timeout error, try splitting and retrying
        if (result.error && isTimeoutError(result.error) && audioBlob.size > 20000) {
          console.log('Timeout detected, splitting audio and retrying...');
          alert.warn('🤔 Calma aí! Ataric está pensando... processando seu áudio com carinho!');
          setTranscript('🤔 Ataric pensando...');
          
          try {
            const [firstHalf, secondHalf] = await splitBlob(audioBlob);
            console.log('Split audio:', { firstSize: firstHalf.size, secondSize: secondHalf.size });
            
            // Process both halves in parallel
            const [result1, result2] = await Promise.all([
              stt.transcribe(firstHalf, language, user.token),
              stt.transcribe(secondHalf, language, user.token)
            ]);
            
            // Combine transcripts
            const transcript1 = result1.transcript || '';
            const transcript2 = result2.transcript || '';
            
            // Check for errors in split results
            if (result1.error === 'Plano expirado!' || result2.error === 'Plano expirado!') {
              alert.warn('Seus 7 minutos gratuitos de hoje chegaram ao fim. Amanhã você pode usar o Ataric grátis novamente. Quer falar sem limites e evoluir mais rápido? Conheça os planos do Ataric 🚀');
              openPlans(navigate);
              return;
            }
            
            if (transcript1 || transcript2) {
              result = { transcript: `${transcript1} ${transcript2}`.trim() };
              console.log('Combined transcript from split:', result.transcript);
            } else {
              // Both halves failed
              result = { error: result1.error || result2.error || 'Falha ao processar áudio' };
            }
          } catch (splitError: any) {
            console.error('Split retry failed:', splitError);
            result = { error: 'Falha ao processar áudio dividido' };
          }
        }
        
        if (result.error) {
          console.error('STT error:', result.error);
          
          if (result.error === 'Plano expirado!') {
            alert.warn('Seus 7 minutos gratuitos de hoje chegaram ao fim. Amanhã você pode usar o Ataric grátis novamente. Quer falar sem limites e evoluir mais rápido? Conheça os planos do Ataric 🚀');
            navigate('/planos');
            return;
          }
          
          if (isTimeoutError(result.error)) {
            alert.warn('😅 Frase muito longa! Tenta algo mais curto?');
          } else {
            // Show visual helper for transcription issues
            console.error('STT error:', result.error);
            setMicError('transcription-failed');
          }
          setFetching(false);
          changeStateAfterStopRec();
          return;
        }
        
        toSend = String(result.transcript || '').trim();
      } else {
        // Native Web Speech API
        recognition.stop();
        await new Promise((r) => setTimeout(r, 150));
        toSend = String(transcriptRef.current || recognition.getText() || transcript || '').trim();
      }
      
      if (!toSend) {
        const err = STT_PROVIDER === 'native' ? (recognition.getLastError?.() || '') : '';
        if (err === 'no-speech') {
          setMicError('no-audio-detected');
        } else {
          setMicError('transcription-failed');
        }
        setFetching(false);
        changeStateAfterStopRec();
        return;
      }

      setLastTranscript(toSend);
      console.log('Transcript received, sending to chat...');
      const answer = await send(toSend);
      if (!answer) {
        setFetching(false);
        changeStateAfterStopRec();
        return;
      }

      await speak(answer);
      
      setFetching(false);
      setAnswerToTranscript(answer);
      changeStateAfterStopRec();
      console.log('rec() completed successfully');

      return;
    } catch (error: any) {
      console.error('Error in rec():', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      
      // Check for timeout/network errors and show friendly message
      if (isTimeoutError(error?.message || '') || error?.name === 'AbortError') {
        alert.warn('😅 Frase muito longa! Tenta algo mais curto?');
      } else {
        console.error('Recording error:', error);
        setMicError('generic-error');
      }
      
      setFetching(false);
      changeStateAfterStopRec();
    }
  }

  function setupVoice(lang = language) {
    voice.load(lang);
  }
  
  function setupRecognition(lang = language) {
    recognition.load(lang);
  }

  async function fetchLatestConversation() {
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/v1/conversations/latest`;
      const headers = { Authorization: user.token };
      const response = await http.get(url, headers);
      
      if (response.error || !response.conversation) {
        return null;
      }
      
      return response;
    } catch (err) {
      console.log('No previous conversation found');
      return null;
    }
  }

  async function setupMessages() {
      if (!user.name) {
        return;
      }

      setFetching(true);

      await resetStartPlanUsage();

      // Check for previous conversation to restore memory
      const previousData = await fetchLatestConversation();
      let previousMessages: any[] = [];
      let existingConversationId: number | null = null;
      
      if (previousData?.conversation && previousData?.messages?.length > 0) {
        // We have previous conversation - load it as context
        existingConversationId = previousData.conversation.id;
        setConversationId(existingConversationId);
        previousMessages = previousData.messages.map((m: any) => ({
          role: m.role,
          content: m.content
        }));
        console.log('Loaded previous conversation with', previousMessages.length, 'messages, id:', existingConversationId);
      }

      // Send initial message with previous history as context
      // Pass existingConversationId directly (don't rely on async state)
      // isNewSession=true tells the backend to show the greeting, not continue automatically
      const payload = await sendToChat(previousMessages, existingConversationId, true);
      if (!payload) {
        setFetching(false);
        return;
      }

      // Set messages: previous + new greeting
      setMessages([...previousMessages, payload]);
      await speak(payload.content);
      setAnswerToTranscript(`${payload.content}`.replace(/"/gi, ''));
      setFetching(false);
  }

  async function resetStartPlanUsage() {
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/v1/user/reset-usage`;
      const headers = { Authorization: user.token };
      await http.post(url, {}, headers);
    } catch (err: any) {
      console.log(err.message);
    }
  }

  function normalizeMessagesForApi(all: any[]) {
    const arr = Array.isArray(all) ? all : [];
    // STRICT MODE: send full history (no trimming) to preserve original behavior.
    // We still strip any system messages from the client; the server owns the system prompt.
    return arr.filter((m) => m && m.role !== 'system');
  }

  async function send(text: string) {
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    
    // Track user messages this session for celebration
    setMessagesThisSession(prev => prev + 1);
    
    console.log('Sending messages to chat:', next);
    const content = await sendToChat(next);

    if (!content) {
      console.log('No content returned from sendToChat');
      return;
    }

    const final = [...next, content];
    setMessages(final);
    return content.content;
  }

  async function postToChat(messages: any, convId?: number | null, isNewSession?: boolean) {
    const url = `${process.env.REACT_APP_API_URL}/api/v1/chat`;
    const payload: any = {
      messages: normalizeMessagesForApi(messages),
      brazilian: isBrazilianMode(),
      prompt: getPromptFromRoute(),
      userName: user.name
    };
    
    // Include conversationId if we have one (use passed convId or state)
    const activeConversationId = convId ?? conversationId;
    if (activeConversationId) {
      payload.conversationId = activeConversationId;
    }

    // Flag to indicate this is a new session (user just opened the app)
    if (isNewSession) {
      payload.isNewSession = true;
    }
    
    const headers = { Authorization: user.token };

    console.log('Posting to chat API:', { url, messageCount: payload.messages.length, conversationId: activeConversationId, isNewSession });
    return await http.post(url, payload, headers);
  }

  async function sendToChat(messages: any, convId?: number | null, isNewSession?: boolean) {
    console.log('sendToChat called with', messages.length, 'messages, convId:', convId, 'isNewSession:', isNewSession);
    const response = await postToChat(messages, convId, isNewSession);
    const { error, choices, conversationId: newConversationId } = response;

    console.log('sendToChat response:', { hasError: !!error, hasChoices: !!choices, conversationId: newConversationId });

    // Save conversationId from the response (first message creates a new conversation)
    if (newConversationId && !conversationId) {
      setConversationId(newConversationId);
      console.log('Conversation started with ID:', newConversationId);
    }

    if (error === 'Token não autorizado!') {
      alert.warn('⏰ Sua sessão expirou. Faça login de novo pra continuar!');
      resetSession();
      return;
    }

    if (error === 'Plano expirado!') {
      alert.warn('Seus 7 minutos gratuitos de hoje chegaram ao fim. Amanhã você pode usar o Ataric grátis novamente. Quer falar sem limites e evoluir mais rápido? Conheça os planos do Ataric 🚀');
      openPlans(navigate);
      return;
    }

    if (error) {
      alert.warn('😕 Ops! Algo inesperado aconteceu. Tenta de novo?');
      console.log(error);
      cancelRec();
      voice.stop();
      tts.pause();
      return;
    }

    if (!choices || !choices[0]) {
      console.error('No choices in response');
      return;
    }

    console.log('Returning message from choices[0]');
    return choices[0].message;
  }

  function resetSession() {
    const payload = { token: null };
    setUser(payload);
    localStorage.setItem('user', JSON.stringify(payload));
  }

  function isBrazilianMode() {
    return state && state.brazilianMode;
  }

  function getPromptFromRoute() {
    if (!state || !state.prompt) {
      return null;
    }

    return state.prompt;
  }

  async function speak(text: string) {
    try {
      // TTS library handles AudioContext management internally
      await tts.play(text, user.token, isBrazilianMode());
    } catch (error: any) {
      // TTS errors are handled gracefully by the library
      // Safari NotAllowedError is expected and handled silently
      // Only log non-autoplay errors
      if (error && error.name !== 'NotAllowedError') {
        console.error('Error playing TTS (non-critical):', error);
      }
    }
  }

  function unlockAudioOnGesture() {
    if (audioUnlocked) return;
  
    // ensure audioCtx exists
    if (!audioCtx) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      audioCtx = new AudioContextClass();
    }
  
    const events = ['touchstart', 'touchend', 'mousedown', 'keydown'];
    const unlock = () => {
      if (!audioCtx) return;
      if (audioCtx.state === 'running') {
        audioUnlocked = true;
        events.forEach(e => document.removeEventListener(e, unlock));
        return;
      }
      audioCtx.resume().then(() => {
        audioUnlocked = true;
        events.forEach(e => document.removeEventListener(e, unlock));
        console.log('🔊 Audio context unlocked');
      });
    };
  
    events.forEach(e => document.addEventListener(e, unlock, false));
  }

  async function start() {
    if ((window as any).__atari_starting_chat) return;
    (window as any).__atari_starting_chat = true;
    try {
      // iOS Safari: unlock audio strictly inside the user gesture.
      await tts.unlock();
      unlockAudioOnGesture();
      console.log('Starting chat session...');
      let lang = isBrazilianMode() ? voice.languages.ptBR : voice.languages.enUS;
      console.log('Language:', lang);
      analytics.chatStarted(isBrazilianMode() ? 'beginner' : 'advanced');
      setHasStarted(true);
      
      setupVoice(lang);
      // Only setup native recognition if using native provider
      if (STT_PROVIDER === 'native') {
        setupRecognition(lang);
      }
      setLanguage(lang);
      console.log('Setting up messages...');
      setLastTranscript('');
      // Note: setupMessages will check for existing conversation and set conversationId
      await setupMessages();
      console.log('Start completed successfully');
    } catch (error: any) {
      console.error('Error in start():', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      alert.warn('😕 Ops! Algo deu errado ao iniciar. Recarregue a página e tente de novo.');
      setHasStarted(false);
      setFetching(false);
    } finally {
      (window as any).__atari_starting_chat = false;
    }
  }

  async function feedback() {
    try {
      setFetching(true);

      const answer = await send('Give me a feedback of our conversation in english.');
      changeStateAfterStopRec();

      if (!answer) {
        setFetching(false);
        return;
      }

      await speak(answer);

      setFetching(false);
      setAnswerToTranscript(answer);
    } catch (error: any) {
      console.error('Error in feedback():', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      alert.warn('😕 Não consegui gerar o feedback. Tenta de novo?');
      setFetching(false);
      changeStateAfterStopRec();
    }
  }

  async function exit() {
    cancelRec();
    voice.stop();
    tts.pause();

    // Show celebration if user sent at least 1 message
    if (messagesThisSession > 0) {
      // Fetch total messages for celebration
      try {
        const url = `${process.env.REACT_APP_API_URL}/api/v1/conversations/quick-stats`;
        const headers = { Authorization: user.token };
        const response = await http.get(url, headers);
        if (response && !response.error) {
          setTotalMessages(response.totalMessages || 0);
        }
      } catch (err) {
        console.error('Failed to fetch stats for celebration:', err);
      }
      setShowCelebration(true);
    } else {
      navigate('/');
    }
  }

  function handleCelebrationClose() {
    setShowCelebration(false);
    // Stay on page and continue practicing
  }

  function handleCelebrationExit() {
    setShowCelebration(false);
    navigate('/');
  }

  useEffect(() => {
    // Ao montar o chat: iniciar direto (monstrinho carregando → fala do Ataric + opções de gravar)
    const minLoadTimeMs = 1500;
    const startedAt = Date.now();
    start()
      .finally(() => {
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, minLoadTimeMs - elapsed);
        setTimeout(() => setInitialChatLoading(false), remaining);
      });
  }, []);

  useEffect(() => {
    // Global error handler for uncaught errors
    window.addEventListener('error', (event) => {
      console.error('Global error caught:', event.error);
      console.error('Error details:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      console.error('Error details:', {
        reason: event.reason,
        stack: event.reason?.stack
      });
    });

    return () => {
      voice.stop();
      tts.pause();
    }
  }, []);

  // Enquanto carrega a abertura do chat: só o monstrinho carregando (user não vê "Iniciar" nem "Clique para Falar" vazio)
  if (initialChatLoading) {
    return (
      <div className="Chat-initial-loading">
        <LoadingPet size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="Chat-header">
        {fetching && messages.length > 0 ? (
          <LoadingPet size="large" />
        ) : (
          <AnimatedAtaric size="large" className="mb-0 Chat-header-fade-in" context="chat" showMessages={!hasStarted} />
        )}
      </div>
      { answerToTranscript && showTranscript && !fetching &&
          <div className="App-section">
            <p>{ answerToTranscript }</p>
            {/* { lastTranscript && <p><b>Você:</b> { lastTranscript }</p> } */}
          </div>
      }
      { !hasStarted && 
        <button
          className="App-button"
          onClick={start}
        >
          Iniciar
        </button>
      }
      { hasStarted && 
        <button
          className={`App-button ${listening ? 'App-button-recording' : ''}`}
          onClick={rec}
          disabled={fetching}
        >
          {/* Audio level fill when recording */}
          {listening && STT_PROVIDER === 'server' && (
            <span 
              className="App-button-level-fill"
              style={{ width: `${Math.min(100, audioLevel * 100)}%` }}
            />
          )}
          <span style={{ position: 'relative', zIndex: 1 }}>
            { fetching && messages.length > 0 ? (
              <span className="Chat-listening-wrap" aria-live="polite">
                <span className="Chat-listening-bars" aria-hidden>
                  <span /><span /><span /><span /><span /><span /><span />
                </span>
                <span className="Chat-listening-label">Ouvindo você...</span>
              </span>
            ) : listening ? (
              STT_PROVIDER === 'server' 
                ? 'Enviar 🎤' 
                : (transcript.trim() ? 'ENVIAR' : 'Ouvindo...')
            ) : 'Clique para Falar' }
          </span>
        </button>
      }
      { hasStarted && answerToTranscript && !listening && !fetching &&
        <button
          className="App-button-no-border mt-10"
          onClick={() => setShowTranscript(!showTranscript)}
          style={{ marginBottom: -10 }}
        >
          { showTranscript ? 'Ocultar Legenda' : 'Mostrar Legenda' }
        </button>
      }
      { listening &&
        <button
          className="App-button-cancel mt-10"
          onClick={cancelRec}
        >
          Cancelar
        </button>
      }
      { !listening &&
        <button
          className="App-button-no-border mt-10"
          onClick={exit}
        >
          Voltar
        </button>
      }
      {/* { transcript && <p><b>Você:</b> { transcript }</p> } */}
      {/* <p style={{ position: 'fixed', bottom: 10, left: 0, right: 0, textAlign: 'center', fontSize: 12, opacity: 0.5, margin: 0 }}>v0.0.5</p> */}
      
      {/* Help Widget - shows tip for when user doesn't know what to say (Brazilian mode only) */}
      {hasStarted && isBrazilianMode() && <HelpWidget />}
      
      {/* Celebration Modal */}
      <CelebrationModal
        isOpen={showCelebration}
        onClose={handleCelebrationClose}
        onExit={handleCelebrationExit}
        messagesThisSession={messagesThisSession}
        totalMessages={totalMessages}
      />
      
      {/* Microphone Helper - shows audio level and error guidance */}
      <MicrophoneHelper
        error={micError}
        onRetry={() => {
          clearMicError();
          rec();
        }}
        onDismiss={clearMicError}
        isRecording={listening && STT_PROVIDER === 'server'}
        audioLevel={audioLevel}
      />

      {/* Bússola - Language mind-map (always available during chat) */}
      {hasStarted && (
        <button
          className="bussola-fab"
          onClick={() => setBussolaOpen(!bussolaOpen)}
          title="Bússola: Mapa de ajuda"
        >
          <i className={`fa-solid ${bussolaOpen ? 'fa-xmark' : 'fa-compass'}`} />
        </button>
      )}
      <Bussola isOpen={bussolaOpen} onClose={() => setBussolaOpen(false)} />
    </>
  );
}

export default Chat;
